import { Injectable, inject } from '@angular/core';
import Session from '../utils/session.service';
import Config from '../utils/config.service';
import AngularBroadcastHelperService from '../services/angular-broadcast-helper.service';
import ServiceRestServices from './service-rest.service';
import adapter from 'webrtc-adapter';
import SignalClient from './signal-client';

declare const Modernizr:any;


@Injectable({ providedIn: 'root' })
export default class CallService {
	private session: Session = inject(Session);
	private config: Config = inject(Config);
	private angularBroadcastHelperService: AngularBroadcastHelperService = inject(AngularBroadcastHelperService);
	private serviceRestServices: ServiceRestServices = inject(ServiceRestServices);
	
	sc: any = undefined;
	authenticated: any = false;
	user: any = undefined;
	manualDisconnect: any = false;
	signalConnectionFailureAlertSent: any = false;
	signalAuthenticationFailureAlertSent: any = false;
	callWindowActive: any = false;
	
	constructor() {
		this.angularBroadcastHelperService.onRootEvent(this.config.getBroadcastConfig().Session.INIT_CALL_CLIENT, (event, session) => {
			this.user = session.user;
			this.manualDisconnect = false;
			this.signalConnectionFailureAlertSent = false;
			this.signalAuthenticationFailureAlertSent = false;
			this.loadSignalClient();
		});
		
		this.angularBroadcastHelperService.onRootEvent(this.config.getBroadcastConfig().Session.LOGOUT, (event) => {
			this.disconnect();
		});
	}
	
	init() {
		// TODO ANGULAR_2 move constructor code here
	}
	
	doesBrowserSupport() {
		if ('mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices) {
			if (Modernizr.peerconnection) {
				if (Modernizr.websocketsbinary) {
					return true;
				} else {
					this.emitCallClientTelemetry('browser.unsupported', { reason: 'websocketsbinary' }, 'error');
					console.error('Modernizr.WebSockets');
					return false;
				}
			} else {
				this.emitCallClientTelemetry('browser.unsupported', { reason: 'peerconnection' }, 'error');
				console.error('Modernizr.PeerConnection');
				return false;
			}
		} else {
			this.emitCallClientTelemetry('browser.unsupported', { reason: 'getUserMedia' }, 'error');
			console.error('Modernizr.GetUserMedia');
			return false;
		}
	}
	
	loadSignalClient() {
		if (this.session.isLoggedIn() && this.doesBrowserSupport()) {
			if (this.sc) {
				this.initSignalClient();
			} else {
				try {
					this.emitCallClientTelemetry('adapter.ready', adapter.browserDetails);
					console.log("Adapter loaded for browser: " + adapter.browserDetails.browser);
					this.sc = new SignalClient();
					this.emitCallClientTelemetry('signalClient.ready');
					this.initSignalClient();
				} catch (err) {
					this.emitCallClientTelemetry('signalClient.initFailed', err, 'error');
					console.error('Failed to initialize bundled SignalClient.', err);
				}
			}
		}
	}
	
	initSignalClient() {
		var isInitialized: any = !!this.sc.socket;
		
		this.sc.onConnectionStateChange = (connectionState) => {
			this.emitCallClientTelemetry('connection.state', { connectionState });
			console.log('this.sc.onConnectionStateChange(', connectionState, ')');
			switch (connectionState) {
				case 'TRYING_TO_CONNECT':
				this.authenticated = false;
				this.angularBroadcastHelperService.rootBroadcast('onConnectionStateChange', 'TRYING_TO_CONNECT');
				break;
				case 'CONNECTED':
				if (this.sc.connectionState !== 'CONNECTED') return; // otherwise buggy - on login, it passes TRYING_TO_CONNECT as well
				this.manualDisconnect = false;
				this.signalConnectionFailureAlertSent = false;
				this.angularBroadcastHelperService.rootBroadcast('onConnectionStateChange', 'CONNECTED');
				this.authenticate();
				break;
				case 'DISCONNECTED':
				this.authenticated = false;
				this.reportSignalConnectionFailureOnce();
				this.angularBroadcastHelperService.rootBroadcast('onConnectionStateChange', 'DISCONNECTED');
				break;
			}
		};
		
		this.sc.onContactUpdate = (parameters) => {
			// this.session.updateContactsOnlineStatus(parameters.contacts);
			this.angularBroadcastHelperService.rootBroadcast('onContactUpdate', parameters);
			console.log('onContactUpdate(', parameters, ')');
		};
		
		this.sc.onRequestCall = (parameters) => {
			this.angularBroadcastHelperService.rootBroadcast('onRequestCall', parameters);
			console.log('onRequestCall(', parameters, ')');
		};
		
		this.sc.onCallAccepted = (parameters) => {
			this.angularBroadcastHelperService.rootBroadcast('onCallAccepted');
			console.log('onCallAccepted(', parameters, ')');
		};
		
		this.sc.onCallEnded = (parameters) => {
			this.angularBroadcastHelperService.rootBroadcast('onCallEnded', parameters.callResolution);
			console.log('onCallEnded(', parameters, ')');
		};
		
		this.sc.onRtcOffer = (parameters) => {
			this.angularBroadcastHelperService.rootBroadcast('onRtcOffer', parameters.offer);
			console.log('onRtcOffer(', parameters, ')');
		};
		
		this.sc.onRtcAnswer = (parameters) => {
			this.angularBroadcastHelperService.rootBroadcast('onRtcAnswer', parameters.answer);
			console.log('onRtcAnswer(', parameters, ')');
		};
		
		this.sc.onRtcIceCandidate = (parameters) => {
			this.angularBroadcastHelperService.rootBroadcast('onRtcIceCandidate', parameters.iceCandidate);
			console.log('onRtcIceCandidate(', parameters, ')');
		};
		
		this.sc.onRtcCustom = (parameters) => {
			console.log('onRtcCustom(', parameters, ')');
			for (var customAction in parameters) {
				switch (customAction) {
					case 'mediaStateChange':
					this.angularBroadcastHelperService.rootBroadcast('onMediaStateChange', parameters.mediaStateChange);
					console.log('onRtcCustom mediaStateChange(', parameters.mediaStateChange, ')');
					break;
					default:
					console.error('ERROR: Unhandled rtcCustom op - "' + parameters + '".');
					break;
				}
			}
		};
		
		var env: any = '{{ENVIRONMENT}}' == this.config.getSettings().ENVIRONMENT ? 'test' : this.config.getSettings().ENVIRONMENT;
		if (!isInitialized) this.sc.connect(null, env);
	}
	
	authenticate() {
		var env: any = '{{ENVIRONMENT}}' == this.config.getSettings().ENVIRONMENT ? 'test' : this.config.getSettings().ENVIRONMENT;
		this.sc.authenticate(env, this.user.id, this.user.sipPassword, this.config.getSettings().PLATFORM,
			(parameters) => {
				this.authenticated = true;
				this.signalAuthenticationFailureAlertSent = false;
				this.emitCallClientTelemetry('authentication.success');
				this.angularBroadcastHelperService.rootEmit(this.config.getBroadcastConfig().Call.AUTHENTICATED);
				console.log('authenticate.onSuccess(', parameters, ')');
			},
			(parameters) => {
				this.authenticated = false;
				this.emitCallClientTelemetry('authentication.failed', parameters, 'error');
				this.reportSignalAuthenticationFailureOnce(parameters);
				this.angularBroadcastHelperService.rootEmit(this.config.getBroadcastConfig().Call.AUTHENTICATION_FAILED, parameters);
				console.error('authenticate.onError(', parameters, ')');
			}
		);
	}
	
	// TODO: Handle this callback madness
	requestCall(endpoint, audioOnly, successCallback, errorCallback) {
		if (this.authenticated) {
			this.sc.requestCall(endpoint, audioOnly,
				(parameters) => {
					console.log('requestCall.onSuccess(', parameters, ')');
					successCallback();
				},
				(parameters) => {
					console.error('requestCall.onError(', parameters, ')');
					errorCallback(parameters);
				}
			);
		} else {
			errorCallback();
			console.error('requestCall.notAuthenticated!');
		}
	}
	
	acceptCall(successCallback, errorCallback) {
		this.sc.acceptCall(
			(parameters) => {
				successCallback();
				console.log('acceptCall.onSuccess(', parameters, ')');
			},
			(parameters) => {
				errorCallback();
				console.error('acceptCall.onError(', parameters, ')');
			}
		);
	}
	
	declineCall(detail, successCallback, errorCallback) {
		this.sc.declineCall(detail,
			(parameters) => {
				successCallback();
				console.log('declineCall.onSuccess(', parameters, ')');
			}, (parameters) => {
				errorCallback();
				console.error('declineCall.onError(', parameters, ')');
			}
		);
	}
	
	hangUpCall(detail) {
		this.sc.hangUpCall(detail,
			(parameters) => {
				console.log('hangUpCall.onSuccess(', parameters, ')');
			},
			(parameters) => {
				console.error('hangUpCall.onError(', parameters, ')');
			}
		);
	}
	
	rtcIceCandidate(candidate, successCallback, errorCallback) {
		this.sc.rtcIceCandidate(candidate,
			(parameters) => {
				successCallback();
				console.log('rtcIceCandidate.onSuccess(', parameters, ')');
			},
			(parameters) => {
				errorCallback();
				console.error('rtcIceCandidate.onError(', parameters, ')');
			}
		);
	}
	
	rtcOffer(offer, successCallback, errorCallback) {
		this.sc.rtcOffer(offer,
			(parameters) => {
				successCallback();
				console.log('rtcOffer.onSuccess(', parameters, ')');
			},
			(parameters) => {
				errorCallback();
				console.error('rtcOffer.onError(', parameters, ')');
			}
		);
	}
	
	rtcAnswer(answer, successCallback, errorCallback) {
		this.sc.rtcAnswer(answer,
			(parameters) => {
				successCallback();
				console.log('rtcAnswer.onSuccess(', parameters, ')');
			}, 
			(parameters) => {
				errorCallback();
				console.error('rtcAnswer.onError(', parameters, ')');
			}
		);
	}
	
	rtcCustom(parameters) {
		this.sc.rtcCustom(parameters,
			(parameters) => {
				console.log('rtcCustom.onSuccess(', parameters, ')');
			},
			(parameters) => {
				console.error('rtcCustom.onError(', parameters, ')');
			}
		);
	}
	
	disconnect() {
		if (this.sc) {
			this.manualDisconnect = true;
			this.sc.disconnect();
		}
	}

	setCallWindowActive(active) {
		this.callWindowActive = !!active;
	}
	
	startRinging() {
		// Audio.pauseAudioGlobally();
		var targetEl: any = document.getElementById('call_ringtone');
		if (targetEl) {
			targetEl.play();
		}
	}
	
	stopRinging() {
		var sound: any = document.getElementById('call_ringtone');
		if (sound) {
			sound.pause();
			sound.currentTime = 0;
		}
	}
	
	startWaitingSound() {
		// Audio.pauseAudioGlobally();
		var targetEl: any = document.getElementById('call_ringbacktone');
		if (targetEl) {
			targetEl.play();
		}
	}
	
	stopWaitingSound() {
		var sound: any = document.getElementById('call_ringbacktone');
		if (sound) {
			sound.pause();
			sound.currentTime = 0;
		}
	}

	emitCallClientTelemetry(event, data?, level = 'info') {
		var payload: any = {
			source: 'CallService',
			event: event,
			level: level,
			data: data,
			timestamp: new Date().toISOString()
		};
		this.angularBroadcastHelperService.rootBroadcast('CALL.CLIENT.TELEMETRY', payload);
		if (level === 'error') {
			console.error('CALL.CLIENT.TELEMETRY', payload);
		} else {
			console.info('CALL.CLIENT.TELEMETRY', payload);
		}
	}

	reportSignalConnectionFailureOnce() {
		if (this.manualDisconnect || this.callWindowActive || this.signalConnectionFailureAlertSent || !this.session.isLoggedIn()) return;
		this.signalConnectionFailureAlertSent = true;
		this.reportCallFailureAlert({
			alertCode: 'SIGNAL_CONNECTION_FAILED',
			message: 'Signal server connection failed',
			detail: 'Incoming call signaling/polling is disconnected',
			source: 'CallService',
			connectionState: this.sc && this.sc.connectionState,
			authenticated: this.authenticated,
			browser: adapter && adapter.browserDetails
		});
	}

	reportSignalAuthenticationFailureOnce(error) {
		if (this.callWindowActive || this.signalAuthenticationFailureAlertSent || !this.session.isLoggedIn()) return;
		this.signalAuthenticationFailureAlertSent = true;
		this.reportCallFailureAlert({
			alertCode: 'SIGNAL_AUTHENTICATION_FAILED',
			message: 'Signal server authentication failed',
			detail: this.getErrorDetail(error),
			source: 'CallService',
			connectionState: this.sc && this.sc.connectionState,
			authenticated: this.authenticated,
			browser: adapter && adapter.browserDetails
		});
	}

	reportCallFailureAlert(payload) {
		this.serviceRestServices.reportCallFailure(payload,
			() => {
				this.emitCallClientTelemetry('callFailureAlert.sent', { alertCode: payload && payload.alertCode });
			},
			(error) => {
				this.emitCallClientTelemetry('callFailureAlert.failed', {
					alertCode: payload && payload.alertCode,
					status: error && error.status
				}, 'error');
			}
		);
	}

	getErrorDetail(error) {
		if (!error) return undefined;
		if (typeof error === 'string') return error;
		var parts: any = [];
		if (error.code) parts.push(error.code);
		if (error.name) parts.push(error.name);
		if (error.message) parts.push(error.message);
		return parts.length ? parts.join(': ') : JSON.stringify(error);
	}
	
}
